import { createFileRoute, Link } from "@tanstack/react-router";
import range from "@/assets/range.jpg";
import { MarketingHeroImage } from "@/components/site/MarketingHeroImage";
import { EmphasizedTitle } from "@/components/site/EmphasizedTitle";
import {
  getDrivingRangeCommunityBullets,
  getDrivingRangeCommunityIntro,
  getDrivingRangeIntro,
  getDrivingRangePracticeFeatures,
  getDrivingRangeTagline,
} from "@/lib/driving-range";
import { HERO_IMAGE_SIZES, heroPreloadLink } from "@/lib/marketing-images";
import { rangePic } from "@/lib/marketing-images.range";
import { SITE_ADDRESS, SITE_MAPS_URL, SITE_PHONE, SITE_PHONE_TEL } from "@/lib/site-info";
import { pageHead } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";
import { ArrowRight, Clock, MapPin, Phone } from "lucide-react";

export const Route = createFileRoute("/range")({
  head: () => {
    const head = pageHead({
      title: m.range_meta_title(),
      description: m.range_meta_description(),
      path: "/range",
      image: range,
    });
    return {
      ...head,
      links: [...(head.links ?? []), heroPreloadLink(rangePic)],
    };
  },
  component: RangePage,
});

function FeatureGrid({ items }: { items: { title: string; body: string }[] }) {
  return (
    <div className="grid gap-px bg-white/10 md:grid-cols-2">
      {items.map((item) => (
        <div key={item.title} className="bg-ink p-10">
          <h3 className="font-display text-2xl">{item.title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground/75">{item.body}</p>
        </div>
      ))}
    </div>
  );
}

function RangePage() {
  const tagline = getDrivingRangeTagline();
  const intro = getDrivingRangeIntro();
  const practiceFeatures = getDrivingRangePracticeFeatures();
  const communityIntro = getDrivingRangeCommunityIntro();
  const communityBullets = getDrivingRangeCommunityBullets();

  return (
    <>
      <section className="relative isolate overflow-hidden bg-ink">
        <MarketingHeroImage
          picture={rangePic}
          alt=""
          sizes={HERO_IMAGE_SIZES}
          priority
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-ink/60" />
        <div className="container-page relative page-hero-pad">
          <span className="eyebrow">{m.range_eyebrow()}</span>
          <h1 className="mt-3 max-w-4xl font-display text-hero leading-[0.95] md:text-8xl">
            <EmphasizedTitle lead={m.range_title_lead()} accent={m.range_title_accent()} stacked />
          </h1>
          <p className="mt-6 max-w-2xl text-foreground/80 md:text-lg">{tagline}</p>
        </div>
      </section>

      <section className="container-page page-follow-pad">
        <p className="max-w-3xl text-foreground/75 leading-relaxed">{intro}</p>

        <div className="mt-16">
          <span className="eyebrow">{m.range_practice_eyebrow()}</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">{m.range_practice_title()}</h2>
          <div className="mt-10">
            <FeatureGrid items={practiceFeatures} />
          </div>
        </div>

        <div className="mt-20">
          <span className="eyebrow">{m.range_community_eyebrow()}</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">{m.range_community_title()}</h2>
          <p className="mt-5 max-w-3xl text-foreground/75 leading-relaxed">{communityIntro}</p>
          <ul className="mt-8 space-y-3 text-sm text-foreground/75">
            {communityBullets.map((item) => (
              <li key={item} className="flex gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-ember-fill" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-20 grid gap-px bg-white/10 md:grid-cols-3">
          <div className="bg-ink p-9">
            <MapPin className="h-6 w-6 text-ember" />
            <h3 className="mt-4 font-display text-xl">{m.contact_location()}</h3>
            <a
              href={SITE_MAPS_URL}
              className="mt-3 inline-block text-sm text-foreground/80 hover:text-ember"
            >
              <bdi dir="ltr">{SITE_ADDRESS.full}</bdi>
            </a>
          </div>
          <div className="bg-ink p-9">
            <Clock className="h-6 w-6 text-ember" />
            <h3 className="mt-4 font-display text-xl">{m.contact_hours()}</h3>
            <p className="mt-3 text-sm text-foreground/80">{m.site_hours()}</p>
          </div>
          <div className="bg-ink p-9">
            <Phone className="h-6 w-6 text-ember" />
            <h3 className="mt-4 font-display text-xl">{m.contact_phone()}</h3>
            <a
              href={SITE_PHONE_TEL}
              className="mt-3 inline-block font-display text-lg tracking-[0.1em] hover:text-ember"
            >
              {SITE_PHONE}
            </a>
          </div>
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-black/40 p-9">
          <div>
            <h3 className="font-display text-3xl">{m.range_plan_visit()}</h3>
            <p className="mt-2 text-sm text-foreground/70">{m.range_plan_hint()}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/prices" className="btn-ember btn-ember-hover">
              {m.range_prices_cta()} <ArrowRight className="h-5 w-5 rtl:rotate-180" />
            </Link>
            <Link
              to="/schedule"
              className="inline-flex items-center gap-2 border border-white/30 px-5 py-3 font-display text-sm tracking-[0.15em] hover:bg-white/5"
            >
              {m.lessons_book()}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
